import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Crown, Lock, Sparkles, X, RefreshCw, Loader2, Clock, Volume2, VolumeX, Zap, Download, Mic, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { purchaseService, PURCHASE_STATUS, isNativeApp } from '../services/purchaseService';
import { adLiteService } from '../services/adService';
import { SubscriptionPaywall } from '../components/SubscriptionPaywall';
import { 
  rewardedAdService, 
  saveFeatureUnlock,
  isFeatureUnlocked,
  consumeFeatureUse,
  getFeatureUnlockStatus,
  canWatchMoreAds,
  getRemainingAdsToday,
} from '../services/rewardedAdService';

/**
 * QUR'AN+ PRO CONTEXT
 *
 * Manages Pro subscription status and feature gating.
 *
 * PURCHASE MODEL: Monthly / Yearly / Lifetime — NO free trial.
 * Google Play Console does not have any Free-trial offer configured on the
 * production base plans (monthly-base, yearly-base) at the time of writing,
 * so the app deliberately does not advertise or simulate a trial. If a store
 * offer is added later, RevenueCat will surface it automatically through
 * `entitlement.periodType` — no code change required.
 *
 * Integrates with:
 * - iOS App Store (via Capacitor + RevenueCat)
 * - Google Play Store (via Capacitor + RevenueCat)
 * - Web: BLOCKED - Must use native app for subscriptions & ads
 *
 * ADMIN ACCESS:
 * - URL param: ?tanzeel_admin=<ACCESS_KEY>
 * - This grants temporary Pro access for testing/preview
 *
 * Pricing:
 * - Never hard-coded. All plan prices come from
 *   `pkg.product.priceString` on the live RevenueCat offering (localised,
 *   currency-correct). If you need to display a price, always read it from
 *   the live offering. Do NOT add fallback strings here.
 */

const ProContext = createContext();

// ADMIN ACCESS: Secret key for owner/developer testing on web
// Configure these in .env file - NEVER hardcode in production
// Usage: Add ?tanzeel_admin=YOUR_ACCESS_KEY to any URL, then enter password
const ADMIN_ACCESS_KEY = process.env.REACT_APP_ADMIN_ACCESS_KEY || '';
const ADMIN_URL_PARAM = 'tanzeel_admin';
const ADMIN_SESSION_KEY = 'tanzeel_admin_session';
const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || '';

// Check if admin access is granted via URL param or session
const checkAdminAccess = () => {
  try {
    // Admin feature disabled if no key configured
    if (!ADMIN_ACCESS_KEY) {
      return { granted: false, needsPassword: false };
    }
    
    // Check if already authenticated this session (password verified)
    if (sessionStorage.getItem(ADMIN_SESSION_KEY) === 'verified') {
      console.log('[Pro] Admin access from verified session');
      return { granted: true, needsPassword: false };
    }
    
    // Check URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const adminParam = urlParams.get(ADMIN_URL_PARAM);
    
    if (adminParam === ADMIN_ACCESS_KEY) {
      // URL param is correct, but need password verification
      console.log('[Pro] Admin URL param detected - password required');
      return { granted: false, needsPassword: true };
    }
    
    return { granted: false, needsPassword: false };
  } catch {
    return { granted: false, needsPassword: false };
  }
};

// Verify admin password
const verifyAdminPassword = (password) => {
  if (!ADMIN_PASSWORD) return false;
  if (password === ADMIN_PASSWORD) {
    sessionStorage.setItem(ADMIN_SESSION_KEY, 'verified');
    return true;
  }
  return false;
};

// Free tier daily limits - UNIFIED 10-CREDIT SYSTEM
// All AI features share the same pool of 10 daily credits
export const FREE_TIER_LIMITS = {
  TOTAL_DAILY_CREDITS: 10,  // Combined limit for all AI features
  // Individual feature types (all cost 1 credit each)
  USTADH_QUESTIONS_PER_DAY: 10,
  HALAL_SCANS_PER_DAY: 10,
  MOSQUE_EXTRACTIONS_PER_DAY: 10,
};

// API endpoint for server-side usage tracking
const API = process.env.REACT_APP_BACKEND_URL;

// ============ RELEASE GATE 1: SERVER-AUTHORITATIVE ENTITLEMENT ============
// Fixes: TA-0034, TA-0037, TA-0038, TA-0063, TA-0064, TA-0065
// The server is the ONLY source of truth for Pro status.
// localStorage is used only for brief caching, never as authority.

/**
 * Verify Pro status with server - THE ONLY AUTHORITATIVE SOURCE
 * @param {string} userId - User ID
 * @returns {Promise<{isPro: boolean, source: string, expiresAt: string|null}>}
 */
const verifyProWithServer = async (userId) => {
  try {
    const response = await fetch(`${API}/api/entitlements/verify/${userId}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    
    if (!response.ok) {
      console.warn('[Pro] Server verification failed:', response.status);
      return { isPro: false, source: 'server_error', expiresAt: null };
    }
    
    const data = await response.json();
    console.log('[Pro] Server verification result:', data);
    
    return {
      isPro: data.is_pro === true,
      source: data.source || 'server',
      expiresAt: data.expires_at || null,
      verifiedAt: data.verified_at || new Date().toISOString()
    };
  } catch (error) {
    console.error('[Pro] Server verification error:', error);
    // FAIL-CLOSED: If we can't reach server, default to free
    return { isPro: false, source: 'network_error', expiresAt: null };
  }
};

/**
 * Cache server verification result for brief period (UX only, not authority)
 * Cache is invalidated on subscription changes
 */
const VERIFICATION_CACHE_KEY = 'tanzeel_entitlement_cache';
const VERIFICATION_CACHE_TTL = 60 * 1000; // 1 minute - for UX smoothness only

const getCachedVerification = () => {
  try {
    const cached = localStorage.getItem(VERIFICATION_CACHE_KEY);
    if (!cached) return null;
    
    const data = JSON.parse(cached);
    const age = Date.now() - data.cachedAt;
    
    if (age > VERIFICATION_CACHE_TTL) {
      localStorage.removeItem(VERIFICATION_CACHE_KEY);
      return null;
    }
    
    return data;
  } catch {
    return null;
  }
};

const setCachedVerification = (result) => {
  try {
    localStorage.setItem(VERIFICATION_CACHE_KEY, JSON.stringify({
      ...result,
      cachedAt: Date.now()
    }));
  } catch {
    // Ignore storage errors
  }
};

const clearVerificationCache = () => {
  try {
    localStorage.removeItem(VERIFICATION_CACHE_KEY);
    // Also clear old localStorage keys that should no longer be trusted
    // These are READ but never WRITTEN as authority anymore
  } catch {
    // Ignore
  }
};

// Storage keys for tracking free tier usage
const USAGE_KEYS = {
  USTADH: 'quranplus_ustadh_usage',
  HALAL: 'quranplus_halal_usage',
  MOSQUE: 'quranplus_mosque_usage',
};

// Get unique user ID for usage tracking
// SECURITY: URL parameter only allowed in development/preview environments
const getUserId = () => {
  // Check for URL parameter override (for mobile testing in dev/preview only)
  const urlParams = new URLSearchParams(window.location.search);
  const proUserParam = urlParams.get('pro_user');
  
  // Only allow URL parameter in preview/development environments
  const isPreviewOrDev = window.location.hostname.includes('preview.emergentagent.com') ||
                         window.location.hostname === 'localhost' ||
                         window.location.hostname === '127.0.0.1';
  
  if (proUserParam && isPreviewOrDev) {
    // Set the user ID from URL parameter and persist it (dev/preview only)
    localStorage.setItem('quranplus_user_id', proUserParam);
    console.log('[PRO] User ID set from URL parameter (dev/preview mode):', proUserParam);
    return proUserParam;
  }
  
  let userId = localStorage.getItem('quranplus_user_id');
  if (!userId) {
    userId = 'user_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
    localStorage.setItem('quranplus_user_id', userId);
  }
  return userId;
};

// SERVER-SIDE USAGE CHECK - MongoDB verified to prevent cache manipulation
const checkServerUsage = async (featureType) => {
  try {
    const response = await fetch(`${API}/api/usage/check`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: getUserId(),
        feature_type: featureType.toLowerCase()
      })
    });
    
    if (!response.ok) throw new Error('Usage check failed');
    
    const data = await response.json();
    return {
      allowed: data.allowed,
      remaining: data.remaining,
      isPro: data.is_pro,
      message: data.message
    };
  } catch (error) {
    console.error('[Usage] Server check failed:', error);
    // Fallback to local check if server fails
    return null;
  }
};

// SERVER-SIDE USAGE INCREMENT - Called after successful AI operation
const incrementServerUsage = async (featureType) => {
  try {
    const response = await fetch(`${API}/api/usage/increment`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: getUserId(),
        feature_type: featureType.toLowerCase()
      })
    });
    
    if (!response.ok) throw new Error('Usage increment failed');
    
    return await response.json();
  } catch (error) {
    console.error('[Usage] Server increment failed:', error);
    return null;
  }
};

// SERVER-SIDE AD REWARD - Grant 1 extra credit
const grantAdReward = async () => {
  try {
    const response = await fetch(`${API}/api/usage/ad-reward`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: getUserId(),
        feature_type: 'ad_reward'
      })
    });
    
    if (!response.ok) throw new Error('Ad reward failed');
    
    return await response.json();
  } catch (error) {
    console.error('[Usage] Ad reward failed:', error);
    return null;
  }
};

// Get today's date key for usage tracking
const getTodayKey = () => new Date().toISOString().split('T')[0];

// Track and check usage for a feature
const trackUsage = (featureKey, limit) => {
  const today = getTodayKey();
  const storageKey = USAGE_KEYS[featureKey];
  
  try {
    const stored = JSON.parse(localStorage.getItem(storageKey) || '{}');
    const todayUsage = stored[today] || 0;
    
    if (todayUsage >= limit) {
      return { allowed: false, remaining: 0, used: todayUsage };
    }
    
    // Increment usage
    stored[today] = todayUsage + 1;
    // Clean old entries (keep only last 7 days)
    const keys = Object.keys(stored).sort().slice(-7);
    const cleaned = {};
    keys.forEach(k => { cleaned[k] = stored[k]; });
    localStorage.setItem(storageKey, JSON.stringify(cleaned));
    
    return { allowed: true, remaining: limit - stored[today], used: stored[today] };
  } catch (e) {
    return { allowed: true, remaining: limit, used: 0 };
  }
};

// Check usage without incrementing
const checkUsage = (featureKey, limit) => {
  const today = getTodayKey();
  const storageKey = USAGE_KEYS[featureKey];
  
  try {
    const stored = JSON.parse(localStorage.getItem(storageKey) || '{}');
    const todayUsage = stored[today] || 0;
    return { allowed: todayUsage < limit, remaining: limit - todayUsage, used: todayUsage };
  } catch (e) {
    return { allowed: true, remaining: limit, used: 0 };
  }
};

// List of all Pro features with descriptions
export const PRO_FEATURES = {
  ZOOM_MODE: {
    id: 'zoom_mode',
    name: 'Zoom View Mode',
    description: 'Immersive single-ayah reading',
    icon: '🔍'
  },
  HALAL_SCANNER: {
    id: 'halal_scanner',
    name: 'Halal Scanner',
    description: 'Scan products for halal verification',
    icon: '🥗'
  },
  SMART_WIDGET: {
    id: 'smart_widget',
    name: 'Smart Widget',
    description: 'Home screen widget with daily verse',
    icon: '📱'
  },
  CUSTOM_DUA: {
    id: 'custom_dua',
    name: 'Custom Du\'a Generator',
    description: 'AI-powered personalized duas',
    icon: '🤲'
  },
  MOSQUE_FINDER: {
    id: 'mosque_finder',
    name: 'Masjid Guide',
    description: 'Find Nearby Mosque',
    icon: '🕌'
  },
  MASJID_CONNECT: {
    id: 'masjid_connect',
    name: 'Masjid Connect',
    description: 'Sync prayer times with your local mosque',
    icon: '📡'
  },
  ALL_RECITERS: {
    id: 'all_reciters',
    name: 'All Reciters',
    description: 'Access to 20+ world-renowned reciters',
    icon: '🎙️'
  },
  ALL_THEMES: {
    id: 'all_themes',
    name: 'All Themes',
    description: 'Premium themes and color schemes',
    icon: '🎨',
    noAdUnlock: true // STRICT PRO ONLY
  },
  NIGHT_VISION: {
    id: 'night_vision',
    name: 'Night Vision Mode',
    description: 'Ultra-dark mode for night reading',
    icon: '🌙',
    noAdUnlock: true // STRICT PRO ONLY
  },
  MIDNIGHT_REFRESH: {
    id: 'midnight_refresh',
    name: 'Auto Midnight Refresh',
    description: 'Automatic prayer time refresh at 23:59',
    icon: '🕛',
    noAdUnlock: true // STRICT PRO ONLY
  },
  AUTO_SCROLL: {
    id: 'auto_scroll',
    name: 'Auto-Scroll Toolbar',
    description: 'Hands-free reading with adjustable speed',
    icon: '📜'
  },
  TAJWEED_COACH: {
    id: 'tajweed_coach',
    name: 'Tajweed Coach AI',
    description: 'Your personal Quran recitation coach with real-time feedback',
    icon: '🎤'
  },
  KHATM_CALENDAR: {
    id: 'khatm_calendar',
    name: '40-Day Khatm Calendar',
    description: 'Structured plan to complete the Quran',
    icon: '📅'
  },
  READING_STATS: {
    id: 'reading_stats',
    name: 'Reading Statistics',
    description: 'Track your reading progress and streaks',
    icon: '📊'
  },
  WIDGET_STYLES: {
    id: 'widget_styles',
    name: 'Widget Styles',
    description: 'Customize your widget appearance',
    icon: '✨'
  },
  TAFSIR: {
    id: 'tafsir',
    name: 'Tafsir',
    description: 'Quranic interpretation and commentary',
    icon: '📖'
  },
  ALL_HADITH_BOOKS: {
    id: 'all_hadith_books',
    name: 'All Hadith Collections',
    description: 'Access all 6 major hadith books',
    icon: '📚'
  },
  OFFLINE: {
    id: 'offline',
    name: 'Offline Mode',
    description: 'Download Quran for offline reading',
    icon: '📴'
  },
  ALL_QIBLA_STYLES: {
    id: 'all_qibla_styles',
    name: 'Premium Qibla Styles',
    description: 'Beautiful Qibla compass themes',
    icon: '🧭'
  },
};

// Widget Style Definitions (12 styles total)
// 4 Theme-based (FREE) + 8 Premium styles
// Each style has two images:
// - imagePath: Square (22% rounded) for home screen app icon
// - fabImagePath: Round (circular) for floating action button in app
export const WIDGET_STYLES = {
  // ===== ALL PREMIUM STYLES (User's Custom Icons) =====
  kidsparadise: {
    id: 'kidsparadise',
    name: 'Kids Paradise',
    description: 'Playful pink gradient',
    isPro: true,
    isThemeDefault: true,
    useImage: true,
    imagePath: '/images/widgets/kids-paradise.png',
    fabImagePath: '/images/widgets/round/kids-paradise_round.png',
    iconName: 'ic_launcher_kidsparadise',
  },
  classic: {
    id: 'classic',
    name: 'Classic',
    description: 'Timeless navy elegance',
    isPro: true,
    isThemeDefault: true,
    useImage: true,
    imagePath: '/images/widgets/classic.png',
    fabImagePath: '/images/widgets/round/classic_round.png',
    iconName: 'ic_launcher_classic',
  },
  emeraldtheme: {
    id: 'emeraldtheme',
    name: 'Emerald',
    description: 'Deep forest green',
    isPro: true,
    isThemeDefault: true,
    useImage: true,
    imagePath: '/images/widgets/emerald-theme.png',
    fabImagePath: '/images/widgets/round/emerald-theme_round.png',
    iconName: 'ic_launcher_emeraldtheme',
  },
  brightnight: {
    id: 'brightnight',
    name: 'Bright Night',
    description: 'Vibrant purple glow',
    isPro: true,
    isThemeDefault: true,
    useImage: true,
    imagePath: '/images/widgets/bright-night.png',
    fabImagePath: '/images/widgets/round/bright-night_round.png',
    iconName: 'ic_launcher_brightnight',
  },
  midnightonyx: {
    id: 'midnightonyx',
    name: 'Midnight Onyx',
    description: 'Deep black elegance',
    isPro: true,
    useImage: true,
    imagePath: '/images/widgets/midnight-onyx.png',
    fabImagePath: '/images/widgets/round/midnight-onyx_round.png',
    iconName: 'ic_launcher_midnightonyx',
  },
  emeralddivine: {
    id: 'emeralddivine',
    name: 'Emerald Divine',
    description: 'Lush sacred green',
    isPro: true,
    useImage: true,
    imagePath: '/images/widgets/emerald-divine.png',
    fabImagePath: '/images/widgets/round/emerald-divine_round.png',
    iconName: 'ic_launcher_emeralddivine',
  },
  pearlcontrast: {
    id: 'pearlcontrast',
    name: 'Pearl Contrast',
    description: 'Striking black & white',
    isPro: true,
    useImage: true,
    imagePath: '/images/widgets/pearl-contrast.png',
    fabImagePath: '/images/widgets/round/pearl-contrast_round.png',
    iconName: 'ic_launcher_pearlcontrast',
  },
  royalamethyst: {
    id: 'royalamethyst',
    name: 'Royal Amethyst',
    description: 'Majestic purple crown',
    isPro: true,
    useImage: true,
    imagePath: '/images/widgets/royal-amethyst.png',
    fabImagePath: '/images/widgets/round/royal-amethyst_round.png',
    iconName: 'ic_launcher_royalamethyst',
  },
  sapphiresky: {
    id: 'sapphiresky',
    name: 'Sapphire Sky',
    description: 'Celestial blue radiance',
    isPro: true,
    useImage: true,
    imagePath: '/images/widgets/sapphire-sky.png',
    fabImagePath: '/images/widgets/round/sapphire-sky_round.png',
    iconName: 'ic_launcher_sapphiresky',
  },
  rubyflame: {
    id: 'rubyflame',
    name: 'Ruby Flame',
    description: 'Passionate crimson glow',
    isPro: true,
    useImage: true,
    imagePath: '/images/widgets/ruby-flame.png',
    fabImagePath: '/images/widgets/round/ruby-flame_round.png',
    iconName: 'ic_launcher_rubyflame',
  },
  bronzeheritage: {
    id: 'bronzeheritage',
    name: 'Bronze Heritage',
    description: 'Ancient artisan warmth',
    isPro: true,
    useImage: true,
    imagePath: '/images/widgets/bronze-heritage.png',
    fabImagePath: '/images/widgets/round/bronze-heritage_round.png',
    iconName: 'ic_launcher_bronzeheritage',
  },
  goldencrescent: {
    id: 'goldencrescent',
    name: 'Golden Crescent',
    description: 'Luminous gold luxury',
    isPro: true,
    useImage: true,
    imagePath: '/images/widgets/golden-crescent.png',
    fabImagePath: '/images/widgets/round/golden-crescent_round.png',
    iconName: 'ic_launcher_goldencrescent',
  },
};

// Free tier limitations
export const FREE_TIER = {
  reciter: '10', // Sa'ud ash-Shuraim (FREE)
  reciterName: "Sa'ud ash-Shuraim",
  theme: 'classic', // Classic theme only
  qiblaStyle: 'classic', // Basic Qibla style
};

export const ProProvider = ({ children }) => {
  const [isPro, setIsPro] = useState(false);
  const [isProLoaded, setIsProLoaded] = useState(false); // Track when Pro status is loaded
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState(null);
  // Survives re-renders and every setUpgradeFeature(null) call site during ad playback.
  // NOTE (2026-02-25): `activeAdFeatureIdRef` and the paywall's Watch-Ad-to-
  // unlock bypass were removed here. Legit rewarded-ad flows (Flow Mode /
  // Tajweed Coach tokens) call `rewardedAdService.showAd()` directly and
  // scope their own reward, so this ref is no longer needed.
  const [isLoading, setIsLoading] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [showAds, setShowAds] = useState(true); // Free users see ads by default
  const [isAdminAccess, setIsAdminAccess] = useState(false); // Admin bypass for owner testing
  const [showAdminPasswordModal, setShowAdminPasswordModal] = useState(false); // Password prompt
  
  // Pending callback fired after a successful subscription purchase, so the
  // action the user was trying to reach (e.g. tapping a Pro feature) can
  // proceed without a second tap.
  const [pendingFeatureCallback, setPendingFeatureCallback] = useState(null);
  
  // Track ad-based unlocks - use state to trigger re-renders
  const [adUnlockedFeatures, setAdUnlockedFeatures] = useState(() => {
    // Initialize from localStorage (not sessionStorage - persists across app restarts)
    const unlocked = new Set();
    try {
      // Check localStorage for tanzeel_unlock_ keys (new system)
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('tanzeel_unlock_')) {
          const featureId = key.replace('tanzeel_unlock_', '');
          const stored = localStorage.getItem(key);
          if (stored) {
            try {
              const data = JSON.parse(stored);
              // Check if still valid (has uses or time remaining)
              if (data.usesRemaining > 0 || (data.expiresAt && Date.now() < data.expiresAt)) {
                unlocked.add(featureId);
              }
            } catch {
              // Invalid JSON, skip
            }
          }
        }
      }
      
      // Also check legacy pro_ad_unlock_ keys in sessionStorage (for backward compat)
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith('pro_ad_unlock_') && sessionStorage.getItem(key) === 'true') {
          const parts = key.replace('pro_ad_unlock_', '').split('_');
          if (parts.length > 0) {
            unlocked.add(parts[0]);
          }
        }
      }
    } catch {
      // Storage not available
    }
    return unlocked;
  });
  
  // Track ad-based unlocks - now uses the new limited unlock system
  const isFeatureUnlockedViaAd = useCallback((featureId) => {
    // Check state first (triggers re-renders)
    if (adUnlockedFeatures.has(featureId)) {
      // Verify it's still valid (has uses/time remaining)
      const status = getFeatureUnlockStatus(featureId);
      if (status.unlocked) return true;
      // Remove from state if no longer valid
      setAdUnlockedFeatures(prev => {
        const next = new Set(prev);
        next.delete(featureId);
        return next;
      });
      return false;
    }
    // Check storage directly
    return isFeatureUnlocked(featureId);
  }, [adUnlockedFeatures]);
  
  const unlockFeatureViaAd = useCallback((featureId) => {
    // Use the new limited unlock system
    saveFeatureUnlock(featureId);
    // Update state to trigger re-renders
    setAdUnlockedFeatures(prev => new Set([...prev, featureId]));
  }, []);

  // Consume a feature use (call when feature action is performed)
  const consumeFeatureUseAction = useCallback((featureId) => {
    if (isPro) return true; // Pro users have unlimited access
    
    // Consume one use of the feature
    const consumed = consumeFeatureUse(featureId);
    
    // Check if feature is still unlocked after consuming
    const status = getFeatureUnlockStatus(featureId);
    if (!status.unlocked) {
      // Remove from state if no longer unlocked
      setAdUnlockedFeatures(prev => {
        const next = new Set(prev);
        next.delete(featureId);
        return next;
      });
    }
    
    return consumed;
  }, [isPro]);

  // Initialize purchase service and check Pro status on mount
  // RELEASE GATE 1 FIX: Server is the ONLY source of truth
  useEffect(() => {
    const initializePurchases = async () => {
      const userId = getUserId();
      
      try {
        console.log('[Pro] Starting server-authoritative verification...');
        
        // STEP 0: Check for ADMIN ACCESS (owner/developer bypass)
        const adminCheck = checkAdminAccess();
        if (adminCheck.granted) {
          console.log('[Pro] ⭐ ADMIN ACCESS GRANTED - Full Pro features enabled');
          setIsAdminAccess(true);
          setIsPro(true);
          setShowAds(false);
          setIsProLoaded(true);
          toast.success('Admin access granted - Pro features unlocked', { duration: 3000 });
          return;
        } else if (adminCheck.needsPassword) {
          console.log('[Pro] Admin URL detected - showing password prompt');
          setShowAdminPasswordModal(true);
          // Continue loading as free user until password is verified
        }
        
        // STEP 1: Check brief cache for UX smoothness (NOT authority)
        const cached = getCachedVerification();
        if (cached) {
          console.log('[Pro] Using cached verification (will re-verify in background)');
          setIsPro(cached.isPro);
          setShowAds(!cached.isPro);
          setIsProLoaded(true);
          
          // Still verify in background to ensure freshness
          verifyProWithServer(userId).then(result => {
            if (result.isPro !== cached.isPro) {
              console.log('[Pro] Background verification changed status:', result);
              setIsPro(result.isPro);
              setShowAds(!result.isPro);
              setCachedVerification(result);
            }
          });
          
          // Initialize ads if not Pro
          if (!cached.isPro) {
            await adLiteService.initialize();
          }
          return;
        }
        
        // STEP 2: Verify with SERVER (the ONLY authority)
        console.log('[Pro] Verifying with server (authoritative)...');
        const serverResult = await verifyProWithServer(userId);
        
        if (serverResult.isPro) {
          console.log('[Pro] Server confirmed Pro status');
          setIsPro(true);
          setShowAds(false);
          setCachedVerification(serverResult);
          setIsProLoaded(true);
          return;
        }
        
        // STEP 3: Check RevenueCat for native app subscriptions
        // This is supplementary - server is still authoritative.
        // We pass the app's stable user id so RevenueCat binds entitlements
        // to the same identity used by the server check.
        if (isNativeApp()) {
          try {
            await purchaseService.initialize(userId);
            const hasRealSubscription = await purchaseService.checkProStatus();
            
            if (hasRealSubscription) {
              console.log('[Pro] RevenueCat found active subscription');
              // Note: This should sync with server via webhook, but respect it
              setIsPro(true);
              setShowAds(false);
              setCachedVerification({ isPro: true, source: 'revenuecat' });
              setIsProLoaded(true);
              return;
            }
          } catch (e) {
            console.log('[Pro] RevenueCat check failed:', e.message);
          }
        }
        
        // STEP 4: User is FREE - no Pro access
        // NOTE: Web trial is REMOVED - it was a security vulnerability (TA-0038, TA-0063)
        console.log('[Pro] User is free tier');
        setIsPro(false);
        setShowAds(true);
        setCachedVerification({ isPro: false, source: 'verified_free' });
        setIsProLoaded(true);
        
        // Initialize ads for free users
        await adLiteService.initialize();
        
      } catch (error) {
        console.error('[Pro] Initialization error:', error);
        
        // FAIL-CLOSED: Default to free on any error
        // Do NOT check localStorage as fallback - that's the vulnerability we're fixing
        console.log('[Pro] Error during init - defaulting to free (fail-closed)');
        setIsPro(false);
        setShowAds(true);
        setIsProLoaded(true);
        
        await adLiteService.initialize();
      }
    };
    
    initializePurchases();
  }, []);

  // -----------------------------------------------------------------------
  // GOOGLE PLAY / REVENUECAT LIVE UNLOCK
  // -----------------------------------------------------------------------
  // The "user paid via Google Play but the app stays locked" bug has THREE
  // classic causes on native:
  //   1. Purchase completed while the app was backgrounded — no re-check on
  //      resume, so isPro never flips to true until the user force-restarts.
  //   2. RevenueCat's entitlement propagation is async — a webhook, cross-
  //      device sync, or delayed receipt validation fires an event AFTER our
  //      initial check ran. Without a listener, ProContext never sees it.
  //   3. ENTITLEMENT_ID env misconfiguration — the resilience layer in
  //      purchaseService.hasActiveEntitlement() handles that.
  //
  // This effect fixes causes (1) and (2). It:
  //   - Subscribes to purchaseService customer-info updates and flips isPro=true
  //     the moment RevenueCat confirms an active entitlement.
  //   - Refreshes customer info whenever the app returns to the foreground
  //     (Capacitor App plugin), catching purchases that completed off-screen.
  useEffect(() => {
    if (!isProLoaded) return;
    let unsubIap = () => {};
    let removeAppListener = () => {};

    // (1) subscribe to RevenueCat updates
    try {
      unsubIap = purchaseService.onCustomerInfoUpdate((_customerInfo, hasPro) => {
        if (hasPro && !isPro) {
          console.log('[Pro] RevenueCat update: entitlement now active — unlocking Pro');
          setIsPro(true);
          setShowAds(false);
          setCachedVerification({ isPro: true, source: 'revenuecat_live_update' });
        }
      });
    } catch (e) {
      console.warn('[Pro] Failed to subscribe to RevenueCat updates:', e?.message);
    }

    // (2) refresh on app resume (native only)
    if (isNativeApp()) {
      (async () => {
        try {
          const { App } = await import('@capacitor/app');
          const handle = await App.addListener('appStateChange', async (state) => {
            if (state.isActive) {
              console.log('[Pro] App resumed — refreshing RevenueCat customer info');
              try {
                const hasPro = await purchaseService.refreshCustomerInfo();
                if (hasPro && !isPro) {
                  console.log('[Pro] Resume detected active entitlement — unlocking Pro');
                  setIsPro(true);
                  setShowAds(false);
                  setCachedVerification({ isPro: true, source: 'revenuecat_resume' });
                }
              } catch (err) {
                console.warn('[Pro] refreshCustomerInfo on resume failed:', err?.message);
              }
            }
          });
          removeAppListener = () => { try { handle.remove(); } catch { /* noop */ } };
        } catch (e) {
          // @capacitor/app not available in web preview — that's fine.
          console.log('[Pro] @capacitor/app not available (web preview) — resume-refresh disabled');
        }
      })();
    }

    return () => {
      try { unsubIap(); } catch { /* noop */ }
      try { removeAppListener(); } catch { /* noop */ }
    };
  }, [isProLoaded, isPro]);

  // Handle admin password verification
  const handleAdminPasswordSubmit = useCallback((password) => {
    if (verifyAdminPassword(password)) {
      setIsAdminAccess(true);
      setIsPro(true);
      setShowAds(false);
      setShowAdminPasswordModal(false);
      toast.success('Admin access granted - Pro features unlocked', { duration: 3000 });
      return true;
    } else {
      toast.error('Incorrect password', { duration: 2000 });
      return false;
    }
  }, []);

  // Removed: Old trial status periodic check - web trial is no longer supported (TA-0038)
  // Trial functionality is now handled exclusively through RevenueCat native apps

  // Update Pro status - MUST only be called after server verification
  // TA-0037 FIX: Do NOT write to localStorage as authoritative source
  const updateProStatus = useCallback(async (status, source = 'unknown') => {
    console.log(`[Pro] Updating Pro status to ${status} (source: ${source})`);
    setIsPro(status);
    setShowAds(!status);
    
    // Update cache only (not authority)
    setCachedVerification({ isPro: status, source, cachedAt: Date.now() });
    
    if (status) {
      await adLiteService.cleanup();
    } else {
      await adLiteService.initialize();
    }
  }, []);

  // Check if free user can use a limited feature
  const checkFreeLimit = useCallback((feature) => {
    if (isPro) return { allowed: true, remaining: Infinity, used: 0 };
    
    switch (feature) {
      case 'USTADH':
        return checkUsage('USTADH', FREE_TIER_LIMITS.USTADH_QUESTIONS_PER_DAY);
      case 'HALAL':
        return checkUsage('HALAL', FREE_TIER_LIMITS.HALAL_SCANS_PER_DAY);
      case 'MOSQUE':
        return checkUsage('MOSQUE', FREE_TIER_LIMITS.MOSQUE_EXTRACTIONS_PER_DAY);
      default:
        return { allowed: true, remaining: Infinity, used: 0 };
    }
  }, [isPro]);

  // Track usage for a limited feature (call when feature is used)
  const trackFreeUsage = useCallback((feature) => {
    if (isPro) return { allowed: true, remaining: Infinity, used: 0 };
    
    switch (feature) {
      case 'USTADH':
        return trackUsage('USTADH', FREE_TIER_LIMITS.USTADH_QUESTIONS_PER_DAY);
      case 'HALAL':
        return trackUsage('HALAL', FREE_TIER_LIMITS.HALAL_SCANS_PER_DAY);
      case 'MOSQUE':
        return trackUsage('MOSQUE', FREE_TIER_LIMITS.MOSQUE_EXTRACTIONS_PER_DAY);
      default:
        return { allowed: true, remaining: Infinity, used: 0 };
    }
  }, [isPro]);

  // ============================================================================
  // SERVER-SIDE PRO-GATE (MongoDB verified - cannot be bypassed by cache clearing)
  // ============================================================================
  
  // Check if user can use an AI feature (calls server to verify)
  const checkProGate = useCallback(async (featureType) => {
    // PRO users always have access
    if (isPro) {
      return { allowed: true, remaining: -1, isPro: true };
    }
    
    // Check server-side usage
    const serverCheck = await checkServerUsage(featureType);
    
    if (serverCheck) {
      return serverCheck;
    }
    
    // Fallback to local check if server fails
    const localCheck = checkFreeLimit(featureType.toUpperCase());
    return { 
      allowed: localCheck.allowed, 
      remaining: localCheck.remaining,
      isPro: false,
      message: localCheck.allowed ? null : `Daily limit of ${FREE_TIER_LIMITS.TOTAL_DAILY_CREDITS} AI features reached`
    };
  }, [isPro, checkFreeLimit]);
  
  // Initialize rewarded ad service at ProProvider mount (app startup), so an ad
  // is preloaded and ready by the time the user first taps a locked feature.
  // Previously this lived inside UpgradeModal's effect, so the preload only
  // started when the modal opened - the 2s wait in showAd() then wasn't enough
  // to catch a real AdMob load, producing "Unable to load ad" on first attempt
  // and repeat prompts on retry (the "Du'a Generator ad gate" bug).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await rewardedAdService.initialize();
        if (cancelled) return;
        await rewardedAdService.preloadAd();
      } catch (e) {
        console.warn('[Pro] Rewarded ad preload at startup failed:', e?.message || e);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  
  // Increment usage after successful AI operation
  const recordUsage = useCallback(async (featureType) => {
    if (isPro) return { success: true };
    
    // Record on server
    const serverResult = await incrementServerUsage(featureType);
    
    // Also record locally as backup
    trackFreeUsage(featureType.toUpperCase());
    
    return serverResult || { success: true };
  }, [isPro, trackFreeUsage]);
  
  // Handle ad reward - grant 1 extra credit
  const handleAdReward = useCallback(async () => {
    const result = await grantAdReward();
    if (result?.success) {
      toast.success(result.message || '1 credit restored!');
      return true;
    }
    return false;
  }, []);

  // Check if a specific feature is available (including ad unlocks)
  const hasFeature = useCallback((featureId) => {
    if (isPro) return true;
    // Check if unlocked via ad in this session
    if (isFeatureUnlockedViaAd(featureId)) return true;
    return false; // All Pro features locked for free users
  }, [isPro, isFeatureUnlockedViaAd]);

  // Show upgrade prompt for a specific feature
  const promptUpgrade = useCallback((feature) => {
    setUpgradeFeature(feature);
    setShowUpgradeModal(true);
  }, []);

  // Request feature access. If Pro (or already unlocked via an in-session
  // ad-based feature-unlock, which is legacy — nothing currently populates
  // that set), grant access immediately. Otherwise open the upgrade modal.
  //
  // NOTE (2026-02-25): The upgrade modal no longer offers a "Watch Ad to
  // unlock this feature" bypass — it is Subscribe / Restore only. The legit
  // rewarded-ad paths that credit tokens (Flow Mode, Tajweed Coach) call
  // `rewardedAdService.showAd()` directly and never go through this modal.
  const requestFeatureAccess = useCallback((feature, onAccessGranted) => {
    const featureId = feature?.id || feature?.name || 'unknown';

    // If Pro or already unlocked via ad, grant access immediately
    if (isPro || isFeatureUnlockedViaAd(featureId)) {
      onAccessGranted?.();
      return true;
    }

    // Show the upgrade modal (Subscribe / Restore only — no ad bypass)
    setUpgradeFeature(feature);
    setPendingFeatureCallback(() => onAccessGranted);
    setShowUpgradeModal(true);
    return false;
  }, [isPro, isFeatureUnlockedViaAd]);

  // Handle purchase — direct call to the store. No trial is advertised or
  // simulated by the app. If the Play/App Store grants a real free-trial
  // offer, RevenueCat reflects it on the returned CustomerInfo automatically.
  const handlePurchase = useCallback(async (plan = 'yearly') => {
    if (!isNativeApp()) {
      console.warn('[Pro] Purchases only available in the iOS/Android app.');
      toast.info('Subscriptions are only available in the iOS/Android app.', {
        duration: 5000,
      });
      return false;
    }

    setIsLoading(true);
    try {
      const result = await purchaseService.purchasePro(plan);

      if (result.status === PURCHASE_STATUS.SUCCESS) {
        updateProStatus(true);
        setShowUpgradeModal(false);
        setUpgradeFeature(null);
        toast.success(result.message || `Successfully subscribed to ${plan} plan!`);
        return true;
      } else if (result.status === PURCHASE_STATUS.CANCELLED) {
        toast.info('Purchase cancelled');
        return false;
      } else {
        toast.error(result.message || 'Purchase failed');
        return false;
      }
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error('Purchase failed. Please try again.');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [updateProStatus]);

  // Restore Purchases - Required by Apple App Store
  //
  // Accepts an optional `{ silent }` flag so background callers (e.g. the
  // DuasPage 403-retry) can suppress user-facing toasts and let the caller
  // decide how to react to the result. UI-triggered restores stay verbose.
  const restorePurchases = useCallback(async (opts = {}) => {
    const silent = !!opts.silent;
    setIsRestoring(true);
    try {
      const result = await purchaseService.restorePurchases();

      if (result.status === PURCHASE_STATUS.RESTORED && result.hasPro) {
        // Force a fresh server-side verification on the very next check —
        // otherwise the Tajweed token allocator, Halal Scanner gate, etc.
        // may still see the (stale) "free" state cached from before the
        // restore and hand out 0 Pro tokens.
        try { clearVerificationCache(); } catch (_) {}
        updateProStatus(true, 'restore_purchases');
        // Belt-and-braces: kick a getCustomerInfo() so any external
        // observers (paywall, home badge) re-render immediately even if
        // the RevenueCat push listener was late.
        try { await purchaseService.refreshCustomerInfo(); } catch (_) {}
        if (!silent) toast.success(result.message);
      } else if (result.status === PURCHASE_STATUS.SUCCESS && !result.hasPro) {
        // Nothing to restore — actively clear any stale cached Pro so we
        // don't leave the UI in a half-locked state. This matters after
        // a device transfer / uninstall+reinstall where the local
        // subscription entry from the previous session might otherwise
        // survive.
        try { clearVerificationCache(); } catch (_) {}
        if (isPro) {
          // We claimed Pro but the server (RevenueCat) disagrees — trust
          // the verified answer.
          updateProStatus(false, 'restore_no_active');
        }
        if (!silent) toast.info(result.message);
      } else {
        // PURCHASE_STATUS.ERROR (or unexpected) — do NOT change Pro state.
        if (!silent) toast.error(result.message || 'Failed to restore purchases');
      }

      return result;
    } catch (error) {
      console.error('Restore error:', error);
      if (!silent) toast.error('Failed to restore purchases. Please try again.');
      return { status: PURCHASE_STATUS.ERROR, hasPro: false, error: error?.message };
    } finally {
      setIsRestoring(false);
    }
  }, [updateProStatus, isPro]);

  // For testing: Toggle Pro status (only in development/web)
  const togglePro = useCallback(() => {
    if (!isNativeApp()) {
      updateProStatus(!isPro);
    }
  }, [isPro, updateProStatus]);

  // State for new subscription paywall
  const [showPaywall, setShowPaywall] = useState(false);
  
  // Show the full subscription paywall
  const openPaywall = useCallback((feature = null) => {
    setUpgradeFeature(feature);
    setShowPaywall(true);
  }, []);
  
  // Handle successful purchase from paywall
  const handlePaywallSuccess = useCallback(async () => {
    console.log('[Pro] Paywall purchase successful, updating status...');
    
    // Immediately update Pro status
    setIsPro(true);
    setShowAds(false);
    setShowPaywall(false);
    setShowUpgradeModal(false);
    setUpgradeFeature(null);
    
    // Clear any cached verification to force fresh check
    clearVerificationCache();
    
    // Cleanup ads
    await adLiteService.cleanup();
    
    // Call pending callback if exists
    if (pendingFeatureCallback) {
      setTimeout(() => {
        pendingFeatureCallback();
        setPendingFeatureCallback(null);
      }, 100);
    }
    
    toast.success('🎉 Welcome to Tanzeen+ Pro! All features unlocked.');
  }, [pendingFeatureCallback]);

  return (
    <ProContext.Provider value={{
      isPro,
      setIsPro: updateProStatus,
      hasFeature,
      promptUpgrade,
      togglePro,
      showUpgradeModal,
      setShowUpgradeModal,
      upgradeFeature,
      FREE_TIER,
      // Purchase functions
      handlePurchase,
      restorePurchases,
      isLoading,
      isRestoring,
      isNativeApp: isNativeApp(),
      isProLoaded, // NEW: Track when Pro status has been determined
      isAdminAccess, // Admin bypass for owner testing
      // Ad-related
      showAds,
      // Free tier usage limits
      checkFreeLimit,
      trackFreeUsage,
      FREE_TIER_LIMITS,
      // NEW: Server-side Pro-Gate (MongoDB verified)
      checkProGate,    // Check if user can use AI feature
      recordUsage,     // Record usage after successful AI operation
      handleAdReward,  // Grant 1 credit after watching ad
      // Ad-based feature unlock (limited uses)
      requestFeatureAccess,
      isFeatureUnlockedViaAd,
      consumeFeatureUseAction, // Consume a feature use
      getFeatureUnlockStatus, // Get remaining uses/time
      canWatchMoreAds, // Check daily ad limit
      getRemainingAdsToday, // Get remaining ads for today
      // NEW: Full subscription paywall
      showPaywall,
      setShowPaywall,
      openPaywall,
      handlePaywallSuccess,
    }}>
      {children}
      
      {/* Upgrade Modal (Subscribe / Restore only — no Watch-Ad-to-unlock path) */}
      {showUpgradeModal && (
        <UpgradeModal
          feature={upgradeFeature}
          onClose={() => {
            setShowUpgradeModal(false);
            setUpgradeFeature(null);
            setPendingFeatureCallback(null);
          }}
          onUpgrade={handlePurchase}
          onRestore={restorePurchases}
          isLoading={isLoading}
          isRestoring={isRestoring}
        />
      )}
      
      {/* Admin Password Modal */}
      {showAdminPasswordModal && (
        <AdminPasswordModal
          onSubmit={handleAdminPasswordSubmit}
          onClose={() => setShowAdminPasswordModal(false)}
        />
      )}
      
      {/* Full Subscription Paywall */}
      {showPaywall && (
        <SubscriptionPaywall
          feature={upgradeFeature}
          onClose={() => {
            setShowPaywall(false);
            setUpgradeFeature(null);
            setPendingFeatureCallback(null);
          }}
          onSuccess={handlePaywallSuccess}
          showCloseButton={true}
        />
      )}
    </ProContext.Provider>
  );
};

export const usePro = () => {
  const context = useContext(ProContext);
  if (!context) {
    throw new Error('usePro must be used within a ProProvider');
  }
  return context;
};

/**
 * ProGate Component
 * 
 * Wraps content that should only be accessible to Pro users.
 * NO visible PRO badges/overlays - just shows the modal on click.
 * Users can watch a mock ad to unlock the feature for this session.
 */
export const ProGate = ({ 
  feature, 
  children, 
  fallback = null,
  showLock = false, // Default: hide lock icons
  inline = false,
  onAccessGranted = null,
}) => {
  const { isPro, requestFeatureAccess, isFeatureUnlockedViaAd } = usePro();
  const featureId = feature?.id || feature?.name || 'unknown';

  // If Pro or unlocked via ad, show children
  if (isPro || isFeatureUnlockedViaAd(featureId)) {
    return children;
  }

  const handleClick = (e) => {
    e.stopPropagation();
    requestFeatureAccess(feature, onAccessGranted);
  };

  // If no fallback provided and inline, show nothing visible but trigger on click
  if (inline) {
    return (
      <button
        onClick={handleClick}
        className="flex items-center text-muted-foreground hover:text-primary transition-colors"
        title={`Tap to unlock ${feature?.name || 'this feature'}`}
        data-testid={`pro-gate-inline-${featureId}`}
      >
        {/* No visible badge - just the children clickable */}
      </button>
    );
  }

  // If fallback provided, render fallback as clickable
  if (fallback) {
    return (
      <div onClick={handleClick} className="cursor-pointer" data-testid={`pro-gate-fallback-${featureId}`}>
        {fallback}
      </div>
    );
  }

  // Render children normally but make the whole thing clickable to trigger modal
  // NO visible overlay - children look normal, but clicking opens the PRO modal
  return (
    <div 
      onClick={handleClick}
      className="cursor-pointer"
      data-testid={`pro-gate-${featureId}`}
    >
      {children}
    </div>
  );
};

/**
 * ProBadge Component
 * 
 * Small badge to indicate Pro features
 */
export const ProBadge = ({ size = 'sm', className = '' }) => {
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full text-[10px] font-bold ${className}`}>
      <Crown className={size === 'sm' ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
      PRO
    </span>
  );
};

/**
 * ProButton Component
 * 
 * Button that handles PRO feature gating (subscription only — no Watch-Ad bypass)
 * NO visible PRO badge - just triggers modal on click
 */
export const ProButton = ({ 
  feature, 
  onClick, 
  children, 
  showBadge = false, // Default: hide badge
  ...props 
}) => {
  const { isPro, requestFeatureAccess, isFeatureUnlockedViaAd } = usePro();
  const featureId = feature?.id || feature?.name || 'unknown';
  const hasAccess = isPro || isFeatureUnlockedViaAd(featureId);

  const handleClick = (e) => {
    if (!hasAccess) {
      e.preventDefault();
      requestFeatureAccess(feature, () => onClick?.(e));
      return;
    }
    onClick?.(e);
  };

  return (
    <Button onClick={handleClick} {...props}>
      {children}
      {showBadge && !hasAccess && <ProBadge className="ml-2" />}
    </Button>
  );
};

/**
 * Upgrade Modal Component - App Store Compliant
 * 
 * NO HARDCODED PRICES - Let App Store / Google Play handle pricing
 * Safe for App Store submission
 * 
 * MONETIZATION MODEL:
 * - Shows limited unlock info (1 scan, 3 uses, 5 minutes)
 * - Shows daily ad limit (5 ads/day max)
 * - "Skip ads forever with Pro" messaging
 */
const UpgradeModal = ({ feature, onClose, onUpgrade, onRestore, isLoading, isRestoring }) => {
  const { t } = useTranslation();
  const { openPaywall } = usePro();
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <Card 
        className="w-full max-w-md overflow-hidden border-2 border-[#C9A227] shadow-2xl bg-[#F1EBDD] relative"
        onClick={(e) => e.stopPropagation()}
        data-testid="upgrade-modal"
      >
        {/* Ornate corner flourishes */}
        <div className="absolute top-2 left-2 w-6 h-6 pointer-events-none opacity-50 z-10">
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M0 12 Q0 0, 12 0 M3 12 Q3 3, 12 3 M6 12 Q6 6, 12 6" stroke="#C9A227" strokeWidth="1" fill="none" strokeLinecap="round" />
          </svg>
        </div>
        <div className="absolute top-2 right-2 w-6 h-6 pointer-events-none opacity-50 z-10 scale-x-[-1]">
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M0 12 Q0 0, 12 0 M3 12 Q3 3, 12 3 M6 12 Q6 6, 12 6" stroke="#C9A227" strokeWidth="1" fill="none" strokeLinecap="round" />
          </svg>
        </div>

        {/* Emerald Header Ribbon */}
        <div className="bg-gradient-to-br from-[#0F4C3A] to-[#125845] px-6 py-5 text-[#F1EBDD] relative">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={onClose}
            className="absolute top-2 right-2 text-[#F1EBDD]/80 hover:text-[#F1EBDD] hover:bg-white/10 h-8 w-8 rounded-full z-20"
            disabled={isLoading || isRestoring}
          >
            <X className="h-4 w-4" />
          </Button>
          <div className="flex items-center justify-center gap-3 mb-1">
            <div className="p-2 bg-[#C9A227]/25 border border-[#C9A227] rounded-xl shadow-inner">
              <Crown className="h-6 w-6 text-[#C9A227]" />
            </div>
            <div className="text-center">
              <h2 className="text-2xl font-bold font-serif tracking-wide text-[#F1EBDD]">
                {t('pro.title', 'Unlock Premium')}
              </h2>
            </div>
          </div>
          <p className="text-center text-[#C9A227] text-xs italic tracking-wide">
            {t('pro.subtitle', 'Get full access to all features')}
          </p>
          {/* Gold divider */}
          <div className="mt-3 flex items-center justify-center gap-2">
            <span className="h-px w-16 bg-gradient-to-r from-transparent to-[#C9A227]/60" />
            <Sparkles className="h-3 w-3 text-[#C9A227]" />
            <span className="h-px w-16 bg-gradient-to-l from-transparent to-[#C9A227]/60" />
          </div>
        </div>

        <CardContent className="p-5 bg-[#F1EBDD]">
          {/* Feature-name callout */}
          {feature && (
            <div className="mb-4 px-4 py-2.5 rounded-lg bg-[#0F4C3A]/8 border border-[#C9A227]/50 text-center">
              <span className="font-serif font-semibold text-[#0F4C3A]">{feature.name}</span>
              <span className="ml-1.5 text-[#4A4A4A] text-xs">{t('pro.isProFeature', 'is a Pro feature')}</span>
            </div>
          )}

          {/* Trial Active Banner */}
          {/* Flagship: Tajweed Coach AI */}
          <div className="p-3 rounded-xl border-2 border-[#C9A227] bg-white/60 mb-4 flex items-center gap-3 shadow-sm">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#0F4C3A] to-[#125845] border-2 border-[#C9A227] flex items-center justify-center shadow-md flex-shrink-0">
              <Mic className="h-6 w-6 text-[#C9A227]" strokeWidth={1.75} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-bold font-serif text-[#0F4C3A] leading-tight">{t('pro.features.tajweedCoachAI', 'Tajweed Coach AI')}</h3>
              <p className="text-[11px] text-[#4A4A4A] leading-snug">{t('pro.features.tajweedCoachDesc', 'Real-time Quran recitation feedback')}</p>
            </div>
          </div>

          {/* Everything included */}
          <div className="mb-4">
            <h3 className="text-xs font-bold font-serif text-[#0F4C3A] flex items-center gap-2 mb-2 uppercase tracking-widest">
              <span className="h-px flex-1 bg-[#C9A227]/40" />
              <span className="whitespace-nowrap">{t('pro.proIncludes', 'Tanzeen+ Pro includes')}</span>
              <span className="h-px flex-1 bg-[#C9A227]/40" />
            </h3>
            <ul className="grid grid-cols-2 gap-x-3 gap-y-1.5">
              {[
                ['pro.features.tajweedCoachAI', 'Tajweed Coach AI'],
                ['pro.features.zoomView', 'Zoom View Mode'],
                ['pro.features.nightVision', 'Night Vision'],
                ['pro.features.reciters', '20+ Reciters'],
                ['pro.features.halalScanner', 'Halal Scanner'],
                ['pro.features.masjidGuide', 'Masjid Guide'],
                ['pro.features.masjidConnect', 'Masjid Connect'],
                ['pro.features.smartWidget', 'Smart Widget'],
                ['pro.features.customDua', 'Custom Du\'a Gen'],
                ['pro.features.themes', 'Premium Themes'],
                ['pro.features.futureUpdates', 'Future Updates'],
              ].map(([tKey, fallback]) => (
                <li key={tKey} className="flex items-center gap-1.5 text-[11px] text-[#0F4C3A]">
                  <Check className="h-3 w-3 text-[#C9A227] flex-shrink-0" strokeWidth={3} />
                  <span className="truncate">{t(tKey, fallback)}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* CTA Section */}
          <div className="space-y-2">
            {/* Web-only helper: on the web there is no purchase surface —
                direct users to the native app. This is the ONE place we
                still surface a platform hint; there is no longer any
                "Watch Ad to unlock" bypass. */}
            {!isNativeApp() && feature && (
              <div className="w-full px-3 py-2 rounded-lg border border-[#C9A227]/50 bg-[#0F4C3A]/5 text-center">
                <div className="flex items-center justify-center gap-1.5 mb-1.5">
                  <Download className="h-3.5 w-3.5 text-[#0F4C3A]" />
                  <p className="text-[11px] font-semibold text-[#0F4C3A]">{t('paywall.downloadToSubscribe', "Download the App to Subscribe")}</p>
                </div>
                <div className="flex gap-1.5 justify-center">
                  <a href="https://apps.apple.com/app/tanzeen-plus/id6744387984" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-2.5 py-1 bg-[#0F4C3A] text-[#F1EBDD] text-[10px] rounded-md hover:bg-[#125845]">
                    <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                    App Store
                  </a>
                  <a href="https://play.google.com/store/apps/details?id=com.quranplus.app" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-2.5 py-1 bg-[#C9A227] text-[#0F4C3A] text-[10px] rounded-md hover:bg-[#B08D1F] font-semibold">
                    <svg className="h-3 w-3" viewBox="0 0 24 24" fill="currentColor"><path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/></svg>
                    Play Store
                  </a>
                </div>
              </div>
            )}

            {/* Main CTA */}
            <Button 
              onClick={() => {
                onClose();
                openPaywall(feature);
              }}
              disabled={isLoading || isRestoring}
              className="w-full bg-gradient-to-r from-[#0F4C3A] to-[#125845] hover:from-[#125845] hover:to-[#166b4f] text-[#F1EBDD] font-bold h-12 text-sm border-2 border-[#C9A227] shadow-lg rounded-xl"
              data-testid="upgrade-button"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('common.loading', 'Loading...')}
                </>
              ) : (
                <>
                  <Crown className="h-4 w-4 mr-2 text-[#C9A227]" />
                  {t('pro.seePricing', 'See Pricing & Subscribe')}
                </>
              )}
            </Button>

            {/* Trust + Restore row */}
            <div className="flex items-center justify-between pt-1">
              <p className="text-[10px] text-[#4A4A4A] flex items-center gap-1">
                <Lock className="h-2.5 w-2.5" />
                {t('pro.securePayment', 'Secure via App Store / Play')}
              </p>
              <Button 
                variant="ghost" 
                onClick={onRestore}
                disabled={isLoading || isRestoring}
                className="h-6 px-2 text-[10px] text-[#0F4C3A] hover:text-[#125845] hover:bg-[#C9A227]/10 font-semibold"
              >
                {isRestoring ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  t('pro.restorePurchases', 'Restore')
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * Admin Password Modal
 * Requires password after URL parameter is detected for extra security
 */
const AdminPasswordModal = ({ onSubmit, onClose }) => {
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  
  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    
    const success = onSubmit(password);
    
    if (!success) {
      setError('Incorrect password');
      setPassword('');
    }
    
    setIsSubmitting(false);
  };
  
  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <Card 
        className="w-full max-w-sm overflow-hidden border border-amber-500/40 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-password-modal"
      >
        {/* Header */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 px-5 py-4 text-white relative">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={onClose}
            className="absolute top-2 right-2 text-white/70 hover:text-white hover:bg-white/10 h-8 w-8"
          >
            <X className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500/20 rounded-lg">
              <Lock className="h-6 w-6 text-amber-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold tracking-tight">Admin Access</h2>
              <p className="text-white/60 text-xs">Enter password to continue</p>
            </div>
          </div>
        </div>

        <CardContent className="p-5">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none transition-all"
                autoFocus
                data-testid="admin-password-input"
              />
              {error && (
                <p className="mt-2 text-sm text-red-500">{error}</p>
              )}
            </div>
            
            <Button 
              type="submit"
              disabled={isSubmitting || !password}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold h-11"
              data-testid="admin-password-submit"
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Unlock Admin Access'
              )}
            </Button>
            
            <p className="text-center text-xs text-gray-500">
              This access is for authorized personnel only.
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProContext;
